import { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { useAllLabourDefinitions } from '../../../labour/hooks/useLabourQueries.ts';
import { LabourDefinitionOption } from '../molecules/LabourDefinitionOption.tsx';
import type { LabourItemDefinitionSummary } from '../../../labour/types/labourItemTypes.ts';
import type { FC } from 'react';

interface LabourItemAutocompleteProps {
  value: LabourItemDefinitionSummary | null;
  onChange: (value: LabourItemDefinitionSummary | null) => void;
  error?: string;
}

/**
 * Organism: Autocomplete for labour item selection
 * Features:
 * - Preloaded data for instant filtering
 * - Client-side filtering
 * - Keyboard navigation
 * - Highlight matching text
 */
export const LabourItemAutocomplete: FC<LabourItemAutocompleteProps> = ({
  value,
  onChange,
  error,
}) => {
  const [inputValue, setInputValue] = useState('');

  const { data, isLoading, isError } = useAllLabourDefinitions();

  // Client-side filtering for instant results
  const filteredDefinitions = useMemo(() => {
    if (!data?.definitions) return [];
    if (!inputValue) return data.definitions;

    const searchLower = inputValue.toLowerCase();
    return data.definitions.filter(def =>
      def.name.toLowerCase().includes(searchLower)
    );
  }, [data?.definitions, inputValue]);

  const options: Array<LabourItemDefinitionSummary> = [...filteredDefinitions];

  return (
    <Box>
      <Autocomplete
        value={value}
        onChange={(_event, newValue) => {
          onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        options={options}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={isLoading}
        renderInput={params => (
          <TextField
            {...params}
            label="Select Labour Item *"
            placeholder="Start typing to filter..."
            error={!!error}
            helperText={error || 'Select from existing labour items'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <LabourDefinitionOption
              definition={option}
              searchTerm={inputValue}
            />
          </li>
        )}
        noOptionsText={
          inputValue.length === 0
            ? 'Type to filter labour items'
            : 'No matching labour items found'
        }
      />

      {isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to load labour items. Please try again.
        </Alert>
      )}
    </Box>
  );
};
